import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  DialogContentText,
  LinearProgress,
  Box,
  Typography,
} from '@mui/material';

import config from '../../js/config';
import { getBearerString } from '../../js/jwt_token';

const DEFAULT_LICENSE_TEXT = `Copyright © 2025 Apptronik Inc. All rights reserved. This is unpublished software code containing confidential and proprietary information belonging to Apptronik and its licensors. Any reproduction, distribution, modification or public display of this code, in whole or in part, without the express written consent of Apptronik is strictly prohibited.`;
const DEFAULT_SOFTWARE_VERSION = 'qtm-2024.1';
const DEFAULT_CONFIG = '';
const DEFAULT_SOURCE_DATASET = 'mocap';
const DEFAULT_OPERATORS = [
  'male_tall',
  'male_short',
  'female_tall',
  'female_short',
];

type DistinctValues = {
  software_version: string[];
  source_dataset: string[];
  subject_id: string[];
};

type UploadRasDatasetDialogProps = {
  open: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const UploadRasDatasetDialog = ({ open, onClose, onUploadSuccess }: UploadRasDatasetDialogProps) => {
  const [configValue, setConfigValue] = useState(DEFAULT_CONFIG);
  const [distinctValues, setDistinctValues] = useState<DistinctValues | null>(null);
  const [softwareVersion, setSoftwareVersion] = useState(DEFAULT_SOFTWARE_VERSION);
  const [sourceDataset, setSourceDataset] = useState(DEFAULT_SOURCE_DATASET);
  const [subjectId, setSubjectId] = useState('');

  const sanitizeInput = (input: string) => {
    return input
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_.-]/g, '');
  };

  const [licenseText, setLicenseText] = useState(DEFAULT_LICENSE_TEXT);
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [timestamp, setTimestamp] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const resetState = () => {
    setConfigValue(DEFAULT_CONFIG);
    setFile(null);
    setError(null);
    setUploading(false);
    setUploadProgress(0);
    setSoftwareVersion(DEFAULT_SOFTWARE_VERSION);
    setSourceDataset(DEFAULT_SOURCE_DATASET);
    setSubjectId('');
    setLicenseText(DEFAULT_LICENSE_TEXT);
    setDuration(null);
    setTimestamp(null);
  };

  useEffect(() => {
    const fetchDistinctValues = async () => {
      try {
        const response = await fetch(config.data_platform_api_url + '/rasdsts/filter_options', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': getBearerString()
          }
        });
        const data = await response.json();
        setDistinctValues(data);
      } catch (error) {
        console.error('Failed to fetch distinct values:', error);
        setError('Failed to load dropdown options.');
      }
    };

    if (open) {
      fetchDistinctValues();
      resetState();
    }
  }, [open]);

  const parseFileHeader = (file: File): Promise<{ duration: number; timestamp: string }> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        if (!text) {
          reject(new Error("File is empty or could not be read."));
          return;
        }

        const lines = text.split('\n').slice(0, 10); // Read first 10 lines
        let frames: number | null = null;
        let frequency: number | null = null;
        let timestamp: string | null = null;

        for (const line of lines) {
          const parts = line.split('\t');
          if (parts.length >= 2) {
            const key = parts[0].trim();
            if (key === 'NO_OF_FRAMES') {
              frames = parseFloat(parts[1].trim());
            } else if (key === 'FREQUENCY') {
              frequency = parseFloat(parts[1].trim());
            } else if (key === 'TIME_STAMP') {
              // The format is "YYYY-MM-DD, HH:MM:SS.ms"
              const dateTimeString = parts[1].trim().replace(',', '');
              const date = new Date(dateTimeString);
              if (!isNaN(date.getTime())) {
                timestamp = date.toISOString();
              }
            }
          }
        }

        if (frames !== null && frequency !== null && frequency > 0 && timestamp !== null) {
          resolve({ duration: frames / frequency, timestamp });
        } else {
          reject(new Error("Could not find NO_OF_FRAMES, FREQUENCY, or TIME_STAMP in the file header."));
        }
      };
      reader.onerror = () => {
        reject(new Error("Failed to read the file."));
      };
      // Read only the first ~2KB of the file to get the header
      reader.readAsText(file.slice(0, 2048));
    });
  };


  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setDuration(null);
    setTimestamp(null);
    setFile(null);

    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      if (selectedFile.name.toLowerCase().endsWith('.tsv')) {
        try {
          const { duration: calculatedDuration, timestamp: parsedTimestamp } = await parseFileHeader(selectedFile);
          setDuration(calculatedDuration);
          setTimestamp(parsedTimestamp);
          setFile(selectedFile);
        } catch (e) {
          const message = e instanceof Error ? e.message : 'An unknown error occurred during file parsing.';
          setError(`Failed to parse file: ${message}`);
          setFile(null);
        }
      } else {
        setError('Please select a .tsv file.');
        setFile(null);
      }
    }
  };

  const handleUpload = () => {
    if (!file || !softwareVersion || !sourceDataset || !subjectId || duration === null || timestamp === null) {
      setError('Please select a valid file and fill out all fields.');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    // 1. Create metadata.json content (updated to latest spec)
    const metadata = {
      metadata_version: "1.0",
      trial_info: {
        name: file.name.replace(/\.tsv$/, ''),
        subject_id: subjectId,
        duration_s: duration,
        description: null
      },
      transformation: {
        type: "MOCAP",
        time: timestamp,
        software_version: softwareVersion,
        ...(configValue ? { config: configValue } : {}),
      },
      inputs: {
        source_dataset: sourceDataset
      }
    };
    <FormControl fullWidth margin="normal">
      <TextField
        label="Config (optional)"
        value={config}
        onChange={(e) => setConfigValue(e.target.value)}
        variant="outlined"
      />
    </FormControl>

    // 2. Create File objects for metadata.json and LICENSE.txt
    const metadataFile = new File([JSON.stringify(metadata, null, 2)], "metadata.json", { type: "application/json" });
    const licenseFile = new File([licenseText], "LICENSE.txt", { type: "text/plain" });

    // 3. Assemble FormData
    const formData = new FormData();
    formData.append('files', file);
    formData.append('files', metadataFile);
    formData.append('files', licenseFile);
    formData.append('data_type', 'RAS_DATASET_TRIAL');

    const userMetadata = {
      software_version: softwareVersion,
      source_dataset: sourceDataset,
      subject_id: subjectId,
    };

    formData.append('user_metadata', JSON.stringify(userMetadata));

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded * 100) / event.total);
        setUploadProgress(progress);
      }
    });

    xhr.open('POST', config.data_platform_api_url + '/data/upload', true);
    xhr.setRequestHeader('Authorization', getBearerString());

    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        onUploadSuccess();
        onClose();
      } else {
        const message = `Upload failed: ${xhr.responseText || 'Unknown error'}`;
        setError(message);
      }
    };

    xhr.onerror = () => {
      setUploading(false);
      const message = 'Network error during upload. Please check your connection.';
      setError(message);
    };

    xhr.send(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Upload Mocap Dataset Trial</DialogTitle>
      <DialogContent>
        {error && <DialogContentText color="error">{error}</DialogContentText>}

        {uploading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <Typography variant="body2" color="textSecondary">Uploading...</Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="textSecondary" align="right">{`${uploadProgress}%`}</Typography>
          </Box>
        )}

        {!uploading && (
          distinctValues ? (
            <>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  freeSolo
                  options={[
                    ...(distinctValues.subject_id || []),
                    ...DEFAULT_OPERATORS,
                  ].filter((v, i, a) => a.indexOf(v) === i)}
                  value={subjectId}
                  inputValue={subjectId}
                  onChange={(event, newValue) => {
                    const sanitized = sanitizeInput(newValue || '');
                    setSubjectId(sanitized);
                  }}
                  onInputChange={(event, newInputValue) => {
                    const sanitized = sanitizeInput(newInputValue);
                    setSubjectId(sanitized);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subject ID"
                      variant="outlined"
                      helperText="Use your name or body type."
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Autocomplete
                  freeSolo
                  options={distinctValues.software_version || []}
                  value={softwareVersion}
                  inputValue={softwareVersion}
                  onChange={(event, newValue) => {
                    const sanitized = sanitizeInput(newValue || '');
                    setSoftwareVersion(sanitized);
                  }}
                  onInputChange={(event, newInputValue) => {
                    const sanitized = sanitizeInput(newInputValue);
                    setSoftwareVersion(sanitized);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Software Version"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  freeSolo
                  options={distinctValues.source_dataset || []}
                  value={sourceDataset}
                  inputValue={sourceDataset}
                  onChange={(event, newValue) => {
                    const sanitized = sanitizeInput(newValue || '');
                    setSourceDataset(sanitized);
                  }}
                  onInputChange={(event, newInputValue) => {
                    const sanitized = sanitizeInput(newInputValue);
                    setSourceDataset(sanitized);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Dataset"
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth margin="normal">
                <TextField
                  label="Config (optional)"
                  value={configValue}
                  onChange={(e) => setConfigValue(e.target.value)}
                  variant="outlined"
                />
              </FormControl>


              <FormControl fullWidth margin="normal">
                <TextField
                  label="License"
                  multiline
                  rows={4}
                  value={licenseText}
                  onChange={(e) => setLicenseText(e.target.value)}
                  variant="outlined"
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Button variant="contained" component="label">
                  Select File
                  <input type="file" hidden onChange={handleFileChange} accept=".tsv" />
                </Button>
                {file && (
                  <DialogContentText>
                    {file.name}
                  </DialogContentText>
                )}
              </FormControl>
            </>
          ) : (
            <DialogContentText>Loading options...</DialogContentText>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>Cancel</Button>
        <Button onClick={handleUpload} disabled={uploading || !file || !softwareVersion || !sourceDataset || !subjectId || duration === null || timestamp === null} color="primary">
          {uploading ? 'Uploading...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadRasDatasetDialog;